// ==========================================
// Buddy — economy, growth and motion constants
// ==========================================
// Every number here is taken from the design doc's spec cards (3d XP ECONOMY,
// 3d BB'S GROWTH, 3d LAYOUT & PLACEMENT, 2d STATE MACHINE). Keep them in one
// place so tuning the loop never means hunting through components.
// ==========================================

import { Accent, Density, GrowthStage } from './buddy.types';

// ------------------------------------------
// XP economy (3d)
// ------------------------------------------

export const XP_CHAT_EXCHANGE = 5;
/** Chat stops paying out past this many XP in a day. */
export const XP_CHAT_SOFT_CAP_PER_DAY = 50;
export const XP_DAILY_FIRST_OPEN = 15;
/** Multiplied by the streak multiplier. */
export const XP_RUN_WORKFLOW = 20;
export const XP_DEFINE_WORKFLOW = 50;
export const XP_WEEKLY_CHALLENGE = 100;

/** Variable-ratio bonus drop: ~1 in 8 completions pays 2-3x. */
export const BONUS_DROP_ODDS = 8;
export const BONUS_DROP_MULTIPLIERS: readonly number[] = [2, 3];

// ------------------------------------------
// Level curve (3d)
// ------------------------------------------
//
// The spec pins six anchors: L2 300 · L3 700 · L5 1800 · L8 4200 · L12 9000 · L20 24000.
// Between anchors the per-level cost rises rather than staying flat, which both keeps
// the curve smooth and reproduces the mock's "Level 7 -> 8 · 580 / 1000 XP" exactly
// (L7 sits at 3200, L8 at 4200).
//
/** Cumulative XP required to *reach* each level, indexed by level. Index 0 is unused. */
export const LEVEL_THRESHOLDS: readonly number[] = [
  0, // unused
  0, // L1
  300, // L2   <- anchor
  700, // L3   <- anchor
  1200, // L4
  1800, // L5  <- anchor
  2400, // L6
  3200, // L7
  4200, // L8  <- anchor
  5100, // L9
  6200, // L10
  7500, // L11
  9000, // L12 <- anchor
  10525, // L13
  12150, // L14
  13875, // L15
  15700, // L16
  17625, // L17
  19650, // L18
  21775, // L19
  24000, // L20 <- anchor
];

export const MAX_TABLED_LEVEL = LEVEL_THRESHOLDS.length - 1;
/** Past the table each level costs this much more than the one before it. */
export const POST_TABLE_STEP = 2325;
export const POST_TABLE_STEP_GROWTH = 100;

// ------------------------------------------
// Streak (3d + 2d)
// ------------------------------------------

/** Multiplier ramps 1.0x on day 1 to 2.0x on day 30. Day 12 lands on 1.4x, as in 3b. */
export const STREAK_MULTIPLIER_MIN = 1.0;
export const STREAK_MULTIPLIER_MAX = 2.0;
export const STREAK_MULTIPLIER_PEAK_DAY = 30;
/** One banked "streak freeze" per month. */
export const STREAK_FREEZES_PER_MONTH = 1;
/** Guardrail: streak nudges capped at 2/day. */
export const STREAK_NUDGE_CAP_PER_DAY = 2;

// ------------------------------------------
// BB growth stages (3d)
// ------------------------------------------

export interface GrowthStageDef {
  stage: GrowthStage;
  label: string;
  minLevel: number;
  maxLevel: number;
  /** Antenna bulb colour — the clearest read on BB's stage. */
  antenna: string;
  blurb: string;
}

export const GROWTH_STAGES: readonly GrowthStageDef[] = [
  {
    stage: 'spark',
    label: 'Spark',
    minLevel: 1,
    maxLevel: 4,
    antenna: '#1D4ED8', // blue-700
    blurb: 'Bare head, blue antenna, three expressions.',
  },
  {
    stage: 'scout',
    label: 'Scout',
    minLevel: 5,
    maxLevel: 9,
    antenna: '#C2410C', // orange-700
    blurb: 'Antenna halo, listening waveform, celebratory unlock.',
  },
  {
    stage: 'pilot',
    label: 'Pilot',
    minLevel: 10,
    maxLevel: 14,
    antenna: '#BE185D', // pink-700
    blurb: 'Side thrusters and arms, a themed pose per workflow type.',
  },
  {
    stage: 'ace',
    label: 'Ace',
    minLevel: 15,
    maxLevel: Number.MAX_SAFE_INTEGER,
    antenna: '#A16207', // yellow-700
    blurb: 'Crown ring, aura, seasonal outfits.',
  },
];

/** Habitat props unlock at 3/6/9/12/15/18 so progress reads even when BB is idle (3d). */
export const HABITAT_PROPS: readonly { id: string; label: string; unlockLevel: number }[] = [
  { id: 'lamp', label: 'Desk lamp', unlockLevel: 3 },
  { id: 'plant', label: 'Plant', unlockLevel: 6 },
  { id: 'shelf', label: 'Shelf', unlockLevel: 9 },
  { id: 'window', label: 'Window', unlockLevel: 12 },
  { id: 'poster', label: 'Poster', unlockLevel: 15 },
  { id: 'pet', label: 'Pet', unlockLevel: 18 },
];

// ------------------------------------------
// Motion (2d STATE MACHINE + 4c)
// ------------------------------------------

/** How long BB holds a reactive expression before falling back to idle. */
export const BB_HAPPY_MS = 1600;
export const BB_CELEBRATORY_MS = 2400;
export const BB_OOPS_MS = 2000;
/** Idle this long with no input and BB dozes. */
export const BB_DOZE_AFTER_MS = 5 * 60 * 1000;
/** Every state change is a crossfade of the same rig. */
export const BB_CROSSFADE_MS = 120;

/** 4c: spring 220ms in, 140ms out, anchored to the bubble's corner. */
export const WAKE_MS = 220;
export const SLEEP_MS = 140;
/** With prefers-reduced-motion the panel crossfades instead. */
export const REDUCED_MOTION_MS = 120;

/** The +XP chip floats and fades over 2.5s (1d). */
export const REWARD_FLOAT_MS = 2500;
/** Milestone banners collapse to a one-line system message after 4s (2d). */
export const MILESTONE_COLLAPSE_MS = 4000;

/** Hold the mic this long for hands-free dictation (3d). */
export const MIC_HOLD_MS = 400;
/** Hands-free stops after this much silence. */
export const MIC_SILENCE_MS = 1500;

// ------------------------------------------
// Placement + sizing (3d LAYOUT & PLACEMENT, 4a)
// ------------------------------------------

export const PANEL_DEFAULT_WIDTH = 340;
/** 1c ships at 300x480 — leaner than 3a because the rail replaces the header. */
export const RAIL_DEFAULT_WIDTH = 300;
export const RAIL_DEFAULT_HEIGHT = 480;
/** The 1c icon rail itself. */
export const QUEST_RAIL_WIDTH = 34;
export const PANEL_MIN_WIDTH = 260;
export const PANEL_MAX_WIDTH = 420;
export const PANEL_DEFAULT_HEIGHT = 620;
export const PANEL_MIN_HEIGHT = 400;
export const PANEL_MAX_HEIGHT = 900;
/** Inset from the screen edge. */
export const PANEL_INSET = 16;
export const BUBBLE_SIZE = 56;
export const EDGE_TAB_WIDTH = 34;
/** Material dialogs sit at 1000; Buddy stays underneath so ERP modals still work. */
export const BUDDY_Z_INDEX = 900;

/** Responsive ladder from 3c — 260 compact / 340 default / 420 comfortable. */
export const DENSITY_BREAKPOINTS: readonly { max: number; density: Density }[] = [
  { max: 299, density: 'compact' },
  { max: 399, density: 'default' },
  { max: Number.MAX_SAFE_INTEGER, density: 'comfortable' },
];

// ------------------------------------------
// Palette (3d STYLE, mapped to ProSuite's light theme)
// ------------------------------------------
//
// Two variants per accent, mirroring _buddy-tokens.scss. ProSuite's accents are
// built for fills and none of them reach 4.5:1 as text on white, so anything the
// user has to *read* uses the ink variant.
//
/** Bars, rings, fills, and large glyphs. */
export const ACCENT_HEX: Readonly<Record<Accent, string>> = {
  action: '#3B82F6', // blue-500
  xp: '#FF6600', // ProSuite brand orange
  streak: '#EAB308', // yellow-500
  badge: '#EC4899', // pink-500
  error: '#EF4444', // red-500
};

/** Text and small glyphs — all >= 4.5:1 on #ffffff and #f9fafb. */
export const ACCENT_INK: Readonly<Record<Accent, string>> = {
  action: '#1D4ED8', // blue-700  6.7:1
  xp: '#C2410C', // orange-700 5.2:1
  streak: '#A16207', // yellow-700 4.9:1
  badge: '#BE185D', // pink-700  6.0:1
  error: '#B91C1C', // red-700   6.5:1
};

/** Unfilled progress track — gray-200, matching $bb-track. */
export const TRACK_HEX = '#E5E7EB';

/** BB's body colour — ProSuite navy, so the mascot reads as part of the product. */
export const BB_BODY = '#28304E';
export const BB_BODY_SOFT = '#3D4870';
export const BB_VISOR = '#1A2035';

/** Chip rail shows this many workflows; the rest collapse behind "+N" (3d). */
export const CHIP_RAIL_VISIBLE = 3;

/** Auto-titles truncate here, matching ChatGPT's sidebar width budget. */
export const TITLE_MAX_CHARS = 40;

export const STORAGE_KEY_PLACEMENT = 'buddy_placement';
export const STORAGE_KEY_THREADS = 'buddy_threads';
export const STORAGE_KEY_ACTIVE_THREAD = 'buddy_active_thread';
export const STORAGE_KEY_PROGRESS = 'buddy_progress';
export const STORAGE_KEY_PREFS = 'buddy_prefs';
